import { useState, useMemo, useRef, useEffect, useCallback } from 'react';

/**
 * Single-line sentence visualization with SVG annotation overlay.
 * Lifted (and trimmed) from the legacy SentenceStudio prototype.
 *
 * The sentence renders as one horizontally-scrolling line of tokens.
 * Above the text: curved arrows (subject→verb, modifies, renames, …).
 * Below the text: solid or dashed underlines for phrases / clauses.
 * Further below: braces for appositives.
 *
 * Collapsible regions (appositives, relative / dependent clauses) replace
 * their tokens with a typed bracket badge ("[…]" / "⟨…⟩") that toggles back.
 *
 * Props:
 *   data:        { sentence, tokens, collapsible, annotations }
 *                (see parseDataAdapter for shape derivation from parse_data)
 *   activeLayer: which annotation layer to render ("phrases" | "clauses" |
 *                "pos" | "appositives" | "subject-verb")
 *   showCollapseToggles: render the row of "[ + dependent clause ]" buttons
 *                that toggle each collapsible region (default: true)
 */

const COLLAPSE_STYLES = {
  appositive: {
    open: '[', close: ']',
    color: '#f59e0b', bg: '#f59e0b10', border: '#f59e0b30',
    tooltip: 'Appositive — click to expand',
  },
  relative: {
    open: '⟨', close: '⟩',
    color: '#a855f7', bg: '#a855f710', border: '#a855f730',
    tooltip: 'Relative clause — click to expand',
  },
  dependent: {
    open: '⟨', close: '⟩',
    color: '#f59e0b', bg: '#f59e0b10', border: '#f59e0b30',
    tooltip: 'Dependent clause — click to expand',
  },
};

const MONO = 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace';

export default function SentenceVisualization({
  data,
  activeLayer = 'phrases',
  showCollapseToggles = true,
}) {
  const [selected, setSelected] = useState(null);
  const [collapsed, setCollapsed] = useState(new Set());
  const [hoveredRegion, setHoveredRegion] = useState(null);
  const [hoverPos, setHoverPos] = useState(null);
  const [containerEl, setContainerEl] = useState(null);
  const containerRef = useCallback((node) => { if (node) setContainerEl(node); }, []);

  // Reset interactive state when the sentence changes underneath us.
  useEffect(() => {
    setSelected(null);
    setCollapsed(new Set());
  }, [data?.sentence]);

  // Reset selection when the active layer changes.
  useEffect(() => { setSelected(null); }, [activeLayer]);

  const hiddenTokenIds = useMemo(() => {
    const hidden = new Set();
    (data?.collapsible || []).forEach((region) => {
      if (collapsed.has(region.id)) {
        for (let i = region.start; i <= region.end; i++) hidden.add(i);
      }
    });
    return hidden;
  }, [data?.collapsible, collapsed]);

  const toggleCollapse = (regionId) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(regionId)) next.delete(regionId);
      else next.add(regionId);
      return next;
    });
    setSelected(null);
  };

  // Annotations filtered by active layer + visibility.
  const activeAnns = useMemo(() => {
    const layerAnns = (data?.annotations || []).filter((a) => a.layer === activeLayer);
    return layerAnns.filter((a) => {
      for (let i = a.start; i <= a.end; i++) {
        if (hiddenTokenIds.has(i)) return false;
      }
      if (a.target !== undefined && hiddenTokenIds.has(a.target)) return false;
      if (a.targetSpan) {
        for (let i = a.targetSpan[0]; i <= a.targetSpan[1]; i++) {
          if (hiddenTokenIds.has(i)) return false;
        }
      }
      return true;
    });
  }, [data?.annotations, activeLayer, hiddenTokenIds]);

  // POS highlight map (tokens get a tinted background).
  const hlMap = useMemo(() => {
    const m = {};
    activeAnns.forEach((a) => {
      if (a.type === 'highlight') {
        for (let i = a.start; i <= a.end; i++) m[i] = a;
      }
    });
    return m;
  }, [activeAnns]);

  const nonHlAnns = activeAnns.filter((a) => a.type !== 'highlight');

  // Build the visible sequence of tokens + collapse badges.
  const visibleItems = useMemo(() => {
    const items = [];
    const regions = data?.collapsible || [];
    let i = 0;
    while (i < (data?.tokens || []).length) {
      const colRegion = regions.find((r) => collapsed.has(r.id) && r.start === i);
      if (colRegion) {
        const depth = regions.filter((r) =>
          collapsed.has(r.id) && r.start <= colRegion.start && r.end >= colRegion.end
        ).length;
        items.push({ type: 'badge', region: colRegion, depth });
        i = colRegion.end + 1;
        continue;
      }
      if (hiddenTokenIds.has(i)) { i++; continue; }
      items.push({ type: 'token', token: data.tokens[i] });
      i++;
    }
    return items;
  }, [data?.tokens, data?.collapsible, collapsed, hiddenTokenIds]);

  // Map token id → enclosing collapsible region id (smallest wins).
  const tokenToRegion = useMemo(() => {
    const expanded = (data?.collapsible || []).filter((r) => !collapsed.has(r.id));
    const m = {};
    const sorted = [...expanded].sort((a, b) => (b.end - b.start) - (a.end - a.start));
    for (const r of sorted) {
      for (let i = r.start; i <= r.end; i++) m[i] = r.id;
    }
    return m;
  }, [data?.collapsible, collapsed]);

  // Layout measurement for the SVG overlay.
  const depKey = `${activeLayer}|${[...collapsed].join(',')}|${data?.sentence}`;
  const { tokenEls, layout } = useMeasuredLayout(containerEl, depKey);

  if (!data?.tokens?.length) return null;

  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
      {/* Collapse toggles row */}
      {showCollapseToggles && (data?.collapsible || []).length > 0 && (
        <div className="px-4 pt-3 pb-2 flex flex-wrap items-center gap-1.5 border-b border-gray-100 dark:border-gray-800">
          <span className="text-[10px] font-semibold uppercase tracking-[0.12em] text-gray-400 dark:text-gray-500 mr-1">
            Structure
          </span>
          {data.collapsible.map((region) => {
            const isCollapsed = collapsed.has(region.id);
            const st = COLLAPSE_STYLES[region.type] || COLLAPSE_STYLES.appositive;
            return (
              <button
                key={region.id}
                onClick={() => toggleCollapse(region.id)}
                style={{
                  borderColor: isCollapsed ? st.border : undefined,
                  background:  isCollapsed ? `${st.color}10` : 'transparent',
                  color:       st.color,
                }}
                className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium rounded-full border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              >
                <span className="font-mono">{isCollapsed ? '+' : '−'}</span>
                {region.label}
              </button>
            );
          })}
        </div>
      )}

      {/* Sentence + annotations */}
      <div className="overflow-x-auto">
        <div
          ref={containerRef}
          style={{
            position: 'relative',
            whiteSpace: 'nowrap',
            padding: '96px 28px 140px',
            minWidth: 'fit-content',
          }}
        >
          <FloatingCollapseTooltip
            hoveredRegion={hoveredRegion}
            hoverPos={hoverPos}
            regions={(data?.collapsible || []).filter((r) => !collapsed.has(r.id))}
          />

          <div>
            {visibleItems.map((item) => {
              if (item.type === 'badge') {
                const region = item.region;
                const style = COLLAPSE_STYLES[region.type] || COLLAPSE_STYLES.appositive;
                return (
                  <span
                    key={`badge-${region.id}`}
                    ref={(el) => (tokenEls.current[`badge-${region.id}`] = el)}
                    onClick={() => toggleCollapse(region.id)}
                    title={`${region.label}: ${region.summary}\nClick to expand`}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      verticalAlign: 'top',
                      padding: '2px 8px',
                      marginLeft: '4px',
                      marginRight: '4px',
                      cursor: 'pointer',
                      borderRadius: '4px',
                      background: style.bg,
                      border: `1px solid ${style.border}`,
                      transition: 'all 0.15s',
                      height: '32px',
                    }}
                  >
                    <span style={{
                      fontSize: '15px', fontFamily: MONO, fontWeight: 600,
                      color: style.color, lineHeight: 1, letterSpacing: '1px',
                    }}>
                      {style.open.repeat(item.depth)}…{style.close.repeat(item.depth)}
                    </span>
                  </span>
                );
              }

              const tok = item.token;
              const hl = hlMap[tok.id];
              const isPunct = tok.pos === 'punctuation';
              const isInSel = selected && tok.id >= selected.start && tok.id <= selected.end;
              const regionId = tokenToRegion[tok.id];

              return (
                <span
                  key={tok.id}
                  ref={(el) => (tokenEls.current[tok.id] = el)}
                  onMouseEnter={regionId ? () => setHoveredRegion(regionId) : undefined}
                  onMouseLeave={regionId ? () => { setHoveredRegion(null); setHoverPos(null); } : undefined}
                  onMouseMove={regionId ? (e) => {
                    if (!containerEl) return;
                    const cr = containerEl.getBoundingClientRect();
                    setHoverPos({ x: e.clientX - cr.left, y: e.clientY - cr.top });
                  } : undefined}
                  onClick={() => {
                    if (regionId) { toggleCollapse(regionId); return; }
                    const ann = activeAnns.find(
                      (a) => tok.id >= a.start && tok.id <= a.end && a.type !== 'highlight'
                    );
                    setSelected(ann || null);
                  }}
                  style={{
                    display: 'inline-flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    verticalAlign: 'top',
                    padding: isPunct ? '0' : '2px 5px',
                    marginLeft: isPunct ? '-2px' : '5px',
                    marginRight: isPunct ? '3px' : '0',
                    cursor: 'pointer',
                    borderRadius: '3px',
                    background: hl ? `${hl.color}12` : isInSel ? 'rgba(99,102,241,0.10)' : 'transparent',
                    transition: 'background 0.1s',
                  }}
                >
                  <span
                    style={{
                      fontSize: '20px', fontWeight: 400, lineHeight: 1, whiteSpace: 'nowrap',
                      color: hl ? hl.color : undefined,
                    }}
                    className={hl ? '' : 'text-gray-700 dark:text-gray-200'}
                  >
                    {tok.text}
                  </span>
                  {hl && (
                    <span style={{
                      fontSize: '8.5px', fontWeight: 700, marginTop: '2px',
                      fontFamily: MONO, color: hl.color, opacity: 0.65,
                      letterSpacing: '0.03em', lineHeight: 1,
                    }}>
                      {hl.label}
                    </span>
                  )}
                </span>
              );
            })}
          </div>

          <AnnotationOverlay
            layout={layout}
            annotations={nonHlAnns}
            selectedId={selected?.id}
            onSelect={setSelected}
          />
        </div>

        <div className="text-center pb-1 text-[9px] font-mono text-gray-300 dark:text-gray-700">
          ← scroll →
        </div>
      </div>

      {/* Selected annotation detail */}
      {selected && selected.description && (
        <div
          className="px-4 py-3 border-t border-gray-100 dark:border-gray-800"
          style={{ borderLeft: `3px solid ${selected.color}` }}
        >
          <p className="text-xs font-semibold mb-1 font-mono" style={{ color: selected.color }}>
            {selected.label}
          </p>
          <p className="text-sm leading-relaxed text-gray-700 dark:text-gray-300">
            {selected.description}
          </p>
        </div>
      )}
    </div>
  );
}

// ─── Layout measurement ───────────────────────────────────────

function useMeasuredLayout(containerEl, deps) {
  const tokenEls = useRef({});
  const [layout, setLayout] = useState(null);

  const measure = useCallback(() => {
    if (!containerEl) return;
    const cRect = containerEl.getBoundingClientRect();
    const rects = {};
    for (const [key, el] of Object.entries(tokenEls.current)) {
      if (!el) continue;
      const r = el.getBoundingClientRect();
      rects[key] = {
        left: r.left - cRect.left,
        top: r.top - cRect.top,
        width: r.width,
        height: r.height,
        right: r.left - cRect.left + r.width,
        bottom: r.top - cRect.top + r.height,
        cx: r.left - cRect.left + r.width / 2,
        cy: r.top - cRect.top + r.height / 2,
      };
    }
    let textTop = Infinity, textBottom = 0;
    for (const r of Object.values(rects)) {
      textTop = Math.min(textTop, r.top);
      textBottom = Math.max(textBottom, r.bottom);
    }
    let contentWidth = 0;
    for (const r of Object.values(rects)) {
      contentWidth = Math.max(contentWidth, r.right);
    }
    setLayout({ rects, textTop, textBottom, contentWidth: contentWidth + 40 });
  }, [containerEl]);

  useEffect(() => {
    measure();
    if (!containerEl) return;
    const ro = new ResizeObserver(() => requestAnimationFrame(measure));
    ro.observe(containerEl);
    return () => ro.disconnect();
  }, [measure, containerEl]);

  useEffect(() => {
    requestAnimationFrame(measure);
  }, [deps, measure]);

  return { tokenEls, layout, measure };
}

// ─── SVG overlay (arrows / underlines / braces) ───────────────

function AnnotationOverlay({ layout, annotations, selectedId, onSelect }) {
  if (!layout) return null;
  const { rects, textTop, textBottom, contentWidth } = layout;

  const spanRect = (start, end) => {
    let left = Infinity, right = -Infinity;
    for (let i = start; i <= end; i++) {
      const r = rects[i];
      if (!r) continue;
      left = Math.min(left, r.left);
      right = Math.max(right, r.right);
    }
    return left < Infinity
      ? { left: left - 2, right: right + 2, cx: (left + right) / 2, width: right - left + 4 }
      : null;
  };

  const centerX = (start, end) => {
    const first = rects[start];
    const last = rects[end || start];
    if (!first || !last) return 0;
    return (first.left + last.right) / 2;
  };

  const arrows = annotations.filter((a) => a.type === 'arrow');
  const underlines = annotations.filter((a) => a.type === 'underline');
  const braces = annotations.filter((a) => a.type === 'brace');

  const elements = [];
  const ABOVE_ZONE = textTop;
  const BELOW_START = textBottom + 6;
  const UL_GAP = 4;

  arrows.forEach((a) => {
    const srcCx = centerX(a.start, a.end);
    const tgtCx = a.targetSpan
      ? centerX(a.targetSpan[0], a.targetSpan[1])
      : centerX(a.target, a.target);
    if (!srcCx || !tgtCx) return;

    const y = ABOVE_ZONE - 4;
    const dist = Math.abs(tgtCx - srcCx);
    const arcH = Math.min(dist * 0.3, 44);
    const midX = (srcCx + tgtCx) / 2;
    const cy = y - arcH;

    const d = `M ${srcCx} ${y} Q ${midX} ${cy} ${tgtCx} ${y}`;
    const angle = Math.atan2(y - cy, tgtCx - midX);
    const aL = 6;
    const isSelected = selectedId === a.id;

    elements.push(
      <g key={a.id} style={{ cursor: 'pointer', pointerEvents: 'all' }}
         onClick={() => onSelect(isSelected ? null : a)}>
        <path d={d} fill="none" stroke={a.color}
              strokeWidth={isSelected ? 2.5 : 1.5}
              opacity={isSelected ? 1 : 0.8} />
        <polygon
          points={`${tgtCx},${y} ${tgtCx - aL * Math.cos(angle - 0.4)},${y - aL * Math.sin(angle - 0.4)} ${tgtCx - aL * Math.cos(angle + 0.4)},${y - aL * Math.sin(angle + 0.4)}`}
          fill={a.color}
        />
        {a.label && (
          <text x={midX} y={cy - 5} textAnchor="middle" fill={a.color}
                fontSize="9.5" fontWeight="600"
                fontFamily="ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace"
                opacity={isSelected ? 1 : 0.7}>
            {a.label}
          </text>
        )}
      </g>
    );
  });

  underlines.forEach((a, ui) => {
    const span = spanRect(a.start, a.end);
    if (!span) return;
    const y = BELOW_START + UL_GAP + ui * 5;
    const isDashed =
      a.label?.includes('relative') ||
      a.label?.includes('subordinate') ||
      a.label?.includes('dependent') ||
      a.label?.includes('concessive');
    const isSelected = selectedId === a.id;

    elements.push(
      <g key={a.id} style={{ cursor: 'pointer', pointerEvents: 'all' }}
         onClick={() => onSelect(isSelected ? null : a)}>
        <line x1={span.left} y1={y} x2={span.right} y2={y}
              stroke={a.color} strokeWidth={isSelected ? 4 : 3}
              strokeLinecap="round"
              strokeDasharray={isDashed ? '7 5' : 'none'}
              opacity={isSelected ? 1 : 0.7} />
        <text x={span.left + 2} y={y + 14} fill={a.color}
              fontSize="10" fontWeight="600" opacity={isSelected ? 0.9 : 0.6}
              fontFamily="ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace">
          {a.label}
        </text>
      </g>
    );
  });

  const braceBaseY = BELOW_START + UL_GAP + underlines.length * 5 + (underlines.length > 0 ? 22 : 0);
  const BRACE_TIP = 16;
  const BRACE_LABEL_GAP = 14;

  braces.forEach((a, bi) => {
    const span = spanRect(a.start, a.end);
    if (!span) return;
    const y = braceBaseY + bi * (BRACE_TIP + BRACE_LABEL_GAP + 6);
    const mid = span.cx;
    const tipY = y + BRACE_TIP;
    const isSelected = selectedId === a.id;
    const dLeft  = `M ${span.left} ${y} C ${span.left} ${y + 8}, ${mid - 20} ${tipY}, ${mid} ${tipY}`;
    const dRight = `M ${mid} ${tipY} C ${mid + 20} ${tipY}, ${span.right} ${y + 8}, ${span.right} ${y}`;

    elements.push(
      <g key={a.id} style={{ cursor: 'pointer', pointerEvents: 'all' }}
         onClick={() => onSelect(isSelected ? null : a)}>
        <path d={dLeft} fill="none" stroke={a.color}
              strokeWidth={isSelected ? 2.5 : 2} strokeLinecap="round"
              opacity={isSelected ? 1 : 0.7} />
        <path d={dRight} fill="none" stroke={a.color}
              strokeWidth={isSelected ? 2.5 : 2} strokeLinecap="round"
              opacity={isSelected ? 1 : 0.7} />
        <circle cx={mid} cy={tipY} r="2" fill={a.color} opacity={isSelected ? 1 : 0.7} />
        <text x={mid} y={tipY + BRACE_LABEL_GAP} textAnchor="middle"
              fill={a.color} fontSize="10" fontWeight="600"
              fontFamily="ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace"
              opacity={isSelected ? 0.9 : 0.65}>
          {a.label}
        </text>
      </g>
    );
  });

  const maxY = braces.length > 0
    ? braceBaseY + braces.length * (BRACE_TIP + BRACE_LABEL_GAP + 6) + 10
    : BELOW_START + UL_GAP + underlines.length * 5 + (underlines.length > 0 ? 24 : 10);

  return (
    <svg style={{
      position: 'absolute', top: 0, left: 0,
      width: contentWidth, height: maxY,
      pointerEvents: 'none', overflow: 'visible',
    }}>
      {elements}
    </svg>
  );
}

function FloatingCollapseTooltip({ hoveredRegion, hoverPos, regions }) {
  if (!hoveredRegion || !hoverPos) return null;
  const region = regions.find((r) => r.id === hoveredRegion);
  if (!region) return null;
  const style = COLLAPSE_STYLES[region.type] || COLLAPSE_STYLES.appositive;

  return (
    <div style={{
      position: 'absolute',
      left: hoverPos.x - 14,
      top: hoverPos.y - 28,
      width: '28px', height: '18px',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: style.bg, border: `1px solid ${style.border}`, borderRadius: '9px',
      zIndex: 10, pointerEvents: 'none', transition: 'left 0.05s, top 0.05s',
    }}>
      <span style={{
        fontSize: '11px', fontWeight: 700, color: style.color,
        lineHeight: 1, letterSpacing: '2px',
      }}>···</span>
    </div>
  );
}
